import { Box, SxProps, Theme } from "@mui/material";
import { OptionalLink } from "src/components-mui/base/optional-link/optional-link";
import { ResponsiveImage } from "src/components-mui/base/responsive-image/responsive-image";
import { IFontSizeResponsiveMap, WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { Container } from "src/components-mui/layout/container";
import { IImage, ILink } from "src/types/common";
import { EnumHorizontalAlignment } from "src/types/common-enum";
import { ISrc } from "src/utils/responsive-image-util";

const FIGURE_SX: SxProps<Theme> = { margin: 0 };
const IMAGE_SX: SxProps<Theme> = { borderRadius: 0.5, width: "100%" };

const HEADING_FONT_SIZE_H2: IFontSizeResponsiveMap = { xs: "3xl", sm: "3xl", md: "4xl" };
const HEADING_FONT_SIZE_H3: IFontSizeResponsiveMap = { xs: "2xl", sm: "2xl", md: "3xl" };

const CONTAINER_CONTENT_WIDTH_PX = { xs: 719, sm: 670, md: 922, lg: 1122 };

function buildImageSrcSet(image: IImage, tabImage?: IImage, mobImage?: IImage): ISrc[] {
    return [
        { url: mobImage?.url ?? tabImage?.url ?? image.url, queryParams: { w: CONTAINER_CONTENT_WIDTH_PX.xs, fit: "max" } },
        { url: tabImage?.url ?? image.url, queryParams: { w: CONTAINER_CONTENT_WIDTH_PX.sm, fit: "max" } },
        { queryParams: { w: CONTAINER_CONTENT_WIDTH_PX.md, fit: "max" } },
        { queryParams: { w: CONTAINER_CONTENT_WIDTH_PX.lg, fit: "max" } },
    ];
}

export interface IImageModuleProps {
    title?: string;
    titleAsH2?: boolean;
    titleAlignment?: EnumHorizontalAlignment;
    image: IImage;
    tabImage?: IImage;
    mobImage?: IImage;
    caption?: string;
    largeCaptionFontSize?: boolean;
    link?: ILink;
}

export const ImageModule = ({
    title,
    titleAsH2,
    titleAlignment,
    image,
    tabImage,
    mobImage,
    caption,
    largeCaptionFontSize,
    link,
}: IImageModuleProps) => (
    <Container>
        {title && (
            <WfHeading
                component={titleAsH2 ? "h2" : "h3"}
                fontSize={titleAsH2 ? HEADING_FONT_SIZE_H2 : HEADING_FONT_SIZE_H3}
                sx={{ mb: [3, 3, 4], textAlign: titleAlignment === EnumHorizontalAlignment.Center ? "center" : "left" }}
            >
                {title}
            </WfHeading>
        )}
        <Box component="figure" sx={FIGURE_SX}>
            <OptionalLink href={link?.url} target={link?.target} underline="none" sx={{ display: "block" }}>
                <ResponsiveImage baseUrl={image.url} alt={image.alt} srcSet={buildImageSrcSet(image, tabImage, mobImage)} sx={IMAGE_SX} />
            </OptionalLink>
            {caption && (
                <Box
                    component="figcaption"
                    sx={{
                        mt: 2,
                    }}
                >
                    <SimpleRichText text={caption} fontSize={largeCaptionFontSize ? ["md", "lg", "lg"] : "xs"} />
                </Box>
            )}
        </Box>
    </Container>
);
